import { X, Sparkles, Wand2, ArrowLeft, Zap, Layers, PenTool } from 'lucide-react'
import { useState } from 'react'
import { useLocale } from '../contexts/LocaleContext'

interface NewProposalModalProps {
  isOpen: boolean
  onClose: () => void
  onStartFromScratch: () => void
  onUseTemplate: () => void
  onAIGenerate?: (prompt: string) => void
}

export default function NewProposalModal({
  isOpen,
  onClose,
  onStartFromScratch,
  onUseTemplate,
  onAIGenerate
}: NewProposalModalProps) {
  const { tx } = useLocale()
  const [showAIInput, setShowAIInput] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')

  if (!isOpen) return null

  const handleAISubmit = () => {
    if (aiPrompt.trim() && onAIGenerate) {
      onAIGenerate(aiPrompt)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-y-auto max-h-[90vh] animate-modal-enter">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 rounded-full hover:bg-slate-100 transition-all z-10"
        >
          <X className="w-5 h-5 text-slate-400" />
        </button>

        {/* Content */}
        <div className="p-4 pt-10 sm:p-8 sm:pt-12">
          {/* Header */}
          <div className="text-center mb-4 sm:mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r bg-accent-100 text-slate-700 text-xs font-medium mb-2 sm:mb-3">
              <Sparkles className="w-3 h-3" />
              Quor Pro
            </div>
            <h2 className="text-lg sm:text-2xl font-bold text-slate-800 mb-1 sm:mb-2">
              {tx('How would you like to start?', 'איך תרצה להתחיל?')}
            </h2>
            <p className="text-sm sm:text-base text-slate-500">
              {tx('Choose the best way to create your proposal', 'בחר את הדרך הטובה ביותר ליצור את ההצעה שלך')}
            </p>
          </div>

          {/* AI Input Section */}
          {showAIInput ? (
            <div className="animate-fade-in">
              <button
                onClick={() => setShowAIInput(false)}
                className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">{tx('Back', 'חזור')}</span>
              </button>

              <div className="bg-accent-50 rounded-2xl p-6 border border-rose-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 rounded-xl bg-accent-500 shadow-lg shadow-rose-200">
                    <Wand2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">{tx('Describe your proposal', 'תאר את ההצעה שלך')}</h3>
                    <p className="text-slate-500 text-sm">{tx('AI will build a custom proposal for you', 'הבינה המלאכותית תבנה הצעה מותאמת אישית עבורך')}</p>
                  </div>
                </div>

                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={tx('For example: A proposal for developing a corporate website for a law firm, including 5 pages, modern design...', 'לדוגמה: הצעה לפיתוח אתר תדמית למשרד עורכי דין, כולל 5 עמודים, עיצוב מודרני...')}
                  className="w-full h-32 px-4 py-3 bg-white border border-rose-200 rounded-xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent resize-none"
                />

                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <Zap className="w-3 h-3" />
                    <span>{tx('Powered by AI', 'מופעל בבינה מלאכותית')}</span>
                  </div>
                  <button
                    onClick={handleAISubmit}
                    disabled={!aiPrompt.trim()}
                    className="px-6 py-3 bg-accent-500 hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white font-medium transition-all flex items-center gap-2 shadow-lg shadow-accent-200"
                  >
                    <Sparkles className="w-4 h-4" />
                    {tx('Generate Proposal', 'צור הצעה')}
                  </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-3">
                  {tx('AI-generated content is not legal advice and does not replace a licensed attorney. Review all content before sending.', 'תוכן שנוצר על ידי AI אינו ייעוץ משפטי ואינו מחליף עורך דין מורשה. בדוק את כל התוכן לפני שליחה.')}
                </p>

                {/* Quick Examples */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {[
                    { en: 'Business consulting proposal', he: 'הצעה לייעוץ עסקי' },
                    { en: 'App development', he: 'פיתוח אפליקציה' },
                    { en: 'Graphic design', he: 'עיצוב גרפי' },
                    { en: 'Social media management', he: 'ניהול רשתות חברתיות' }
                  ].map((example, i) => (
                    <button
                      key={i}
                      onClick={() => setAiPrompt(tx(example.en, example.he))}
                      className="px-3 py-1.5 bg-white hover:bg-rose-50 border border-rose-200 rounded-lg text-xs text-slate-600 hover:text-rose-600 transition-all"
                    >
                      {tx(example.en, example.he)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Options Grid */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-5">
              {/* Option 1: Start from Scratch */}
              <button
                onClick={onStartFromScratch}
                className="group relative bg-white border-2 border-slate-200 hover:border-blue-400 rounded-2xl p-4 sm:p-6 transition-all hover:shadow-xl hover:-translate-y-1 text-left"
              >
                {/* Illustration */}
                <div className="h-24 sm:h-40 mb-3 sm:mb-4 rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center overflow-hidden">
                  <div className="relative w-full h-full p-4">
                    {/* Document mockup */}
                    <div className="absolute top-4 right-4 left-4 bottom-4 bg-white rounded-lg shadow-md border border-slate-100 p-3">
                      {/* Header line */}
                      <div className="h-3 w-16 bg-blue-400 rounded mb-3" />
                      {/* Content lines */}
                      <div className="space-y-2">
                        <div className="h-2 w-full bg-slate-100 rounded" />
                        <div className="h-2 w-4/5 bg-slate-100 rounded" />
                        <div className="h-2 w-3/4 bg-slate-100 rounded" />
                      </div>
                      {/* Cursor */}
                      <div className="absolute bottom-6 left-6 w-0.5 h-4 bg-blue-500 animate-pulse" />
                    </div>
                    {/* Floating pen icon */}
                    <div className="absolute bottom-2 right-2 p-2 bg-blue-500 rounded-lg shadow-lg transform group-hover:scale-110 transition-transform">
                      <PenTool className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>

                <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-1">{tx('Blank Page', 'דף ריק')}</h3>
                <p className="text-xs sm:text-sm text-slate-500">
                  {tx('Start from scratch with a blank canvas and full control', 'התחל מאפס עם דף ריק ושליטה מלאה')}
                </p>
              </button>

              {/* Option 2: AI Generate - Featured */}
              <button
                onClick={() => setShowAIInput(true)}
                className="group relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-4 sm:p-6 transition-all hover:shadow-xl hover:-translate-y-1 text-left overflow-hidden"
              >
                {/* Subtle grid pattern overlay */}
                <div className="absolute inset-0 opacity-10" style={{
                  backgroundImage: 'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }} />

                {/* Badge */}
                <div className="absolute top-3 right-3 px-2.5 py-1 bg-gradient-to-r from-slate-500 to-slate-500 rounded-md text-[10px] font-semibold text-white">
                  AI
                </div>

                {/* Illustration */}
                <div className="h-24 sm:h-40 mb-3 sm:mb-4 rounded-xl bg-gradient-to-br from-slate-700/50 to-slate-800/50 flex items-center justify-center overflow-hidden border border-slate-700/50">
                  <div className="relative w-full h-full p-4">
                    {/* Document being generated visualization */}
                    <div className="absolute top-4 right-4 left-4 bottom-4 bg-slate-700/60 rounded-lg p-3 border border-slate-600/50">
                      {/* Animated typing lines */}
                      <div className="space-y-2">
                        <div className="h-2 w-20 bg-gradient-to-r from-slate-400 to-slate-400 rounded animate-pulse" />
                        <div className="h-1.5 w-full bg-slate-600 rounded opacity-60" />
                        <div className="h-1.5 w-4/5 bg-slate-600 rounded opacity-40" />
                        <div className="h-1.5 w-3/5 bg-slate-600 rounded opacity-20" />
                      </div>
                      {/* Cursor line */}
                      <div className="mt-3 h-1.5 w-2/3 bg-slate-600 rounded relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-slate-400/50 to-transparent animate-pulse" />
                      </div>
                    </div>
                    {/* AI badge floating */}
                    <div className="absolute bottom-2 right-2 p-2 bg-gradient-to-r from-slate-500 to-slate-500 rounded-lg shadow-lg">
                      <Wand2 className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>

                <h3 className="text-base sm:text-lg font-bold text-white mb-1">{tx('Create with AI', 'צור עם בינה מלאכותית')}</h3>
                <p className="text-xs sm:text-sm text-slate-400">
                  {tx('Describe the project and get a professional proposal', 'תאר את הפרויקט וקבל הצעה מקצועית')}
                </p>
              </button>

              {/* Option 3: Use Template */}
              <button
                onClick={onUseTemplate}
                className="group relative bg-white border-2 border-slate-200 hover:border-emerald-400 rounded-2xl p-4 sm:p-6 transition-all hover:shadow-xl hover:-translate-y-1 text-left"
              >
                {/* Illustration */}
                <div className="h-24 sm:h-40 mb-3 sm:mb-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center overflow-hidden">
                  <div className="relative w-full h-full p-3">
                    {/* Grid of template cards */}
                    <div className="grid grid-cols-2 gap-2 h-full">
                      <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-2 transform group-hover:scale-105 transition-transform">
                        <div className="h-2 w-8 bg-emerald-400 rounded mb-2" />
                        <div className="h-1.5 w-full bg-slate-100 rounded mb-1" />
                        <div className="h-1.5 w-3/4 bg-slate-100 rounded" />
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-2 transform group-hover:scale-105 transition-transform delay-75">
                        <div className="h-2 w-8 bg-teal-400 rounded mb-2" />
                        <div className="h-1.5 w-full bg-slate-100 rounded mb-1" />
                        <div className="h-1.5 w-3/4 bg-slate-100 rounded" />
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-2 transform group-hover:scale-105 transition-transform delay-100">
                        <div className="h-2 w-8 bg-cyan-400 rounded mb-2" />
                        <div className="h-1.5 w-full bg-slate-100 rounded mb-1" />
                        <div className="h-1.5 w-3/4 bg-slate-100 rounded" />
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-2 transform group-hover:scale-105 transition-transform delay-150">
                        <div className="h-2 w-8 bg-green-400 rounded mb-2" />
                        <div className="h-1.5 w-full bg-slate-100 rounded mb-1" />
                        <div className="h-1.5 w-3/4 bg-slate-100 rounded" />
                      </div>
                    </div>
                    {/* Floating layers icon */}
                    <div className="absolute -bottom-1 -right-1 p-2 bg-emerald-500 rounded-lg shadow-lg transform group-hover:scale-110 transition-transform">
                      <Layers className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>

                <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-1">{tx('Ready Template', 'תבנית מוכנה')}</h3>
                <p className="text-xs sm:text-sm text-slate-500">
                  {tx('Choose from a library of professional templates', 'בחר מתוך ספריית תבניות מקצועיות')}
                </p>
              </button>
            </div>
          )}

          {/* Footer */}
          {!showAIInput && (
            <div className="mt-4 sm:mt-8 pt-4 sm:pt-5 border-t border-slate-100 text-center">
              <p className="text-xs text-slate-400">
                {tx('All options include real-time preview and PDF export', 'כל האפשרויות כוללות תצוגה מקדימה בזמן אמת וייצוא ל-PDF')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes modal-enter {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-modal-enter { animation: modal-enter 0.3s ease-out; }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
        .animate-spin-slow { animation: spin-slow 8s linear infinite; }
        .delay-75 { animation-delay: 75ms; }
        .delay-100 { animation-delay: 100ms; }
        .delay-150 { animation-delay: 150ms; }
        .delay-300 { animation-delay: 300ms; }
      `}</style>
    </div>
  )
}
